import { environmentManager, notifyManager, QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/apiCall";
import { queryKeys } from "@/lib/queryKeys";

/**
 * The browser's cache of what the screens read (docs/data-layer.md). The defaults keep the refresh rules the screens had before
 * the cache: a read is trusted for as long as a job page's read-ahead was (30 s), a write the screen makes re-reads what it
 * changed, and nothing is re-read on its own (no re-read when the window regains focus or the network returns). The one addition is
 * a single retry of a read that never got an answer.
 */
export const READ_FRESH_MS = 30_000;

/**
 * A read that never got an answer (the connection dropped, or the browser aborted it when the network changed) is tried once more;
 * a refusal is an answer and is never asked again.
 */
export const retryUnanswered = (failureCount: number, error: Error): boolean =>
  failureCount < 1 && !(error instanceof ApiError);

/** The cache with the screens' rules. `retry: false` is the server's: a read it cannot make at once is left for the browser. */
export function makeQueryClient({
  retry = retryUnanswered,
}: { retry?: typeof retryUnanswered | false } = {}): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: READ_FRESH_MS,
        retry,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      mutations: { retry: false },
    },
  });
  // Nothing observes a Scope of Work ETag; it waits, however long, for the next write to send it.
  client.setQueryDefaults(queryKeys.scopeEtags, { gcTime: Infinity });
  return client;
}

/**
 * `subscribe` for `useSyncExternalStore` over what the cache holds. A change is told a moment later, not at once: the cache also
 * changes while a component renders (HydrationBoundary adds the reads a list page was drawn with), and a store that answered
 * inside that render would make React update the component it belongs to (shell, list) while another is still rendering.
 * useQuery's own observers are told this way too. React reads the snapshot again after the render, so nothing is shown stale.
 */
export const watchCache =
  (client: QueryClient) =>
  (changed: () => void): (() => void) =>
    client.getQueryCache().subscribe(notifyManager.batchCalls(changed));

let browserClient: QueryClient | undefined;

/**
 * One client for the browser's whole visit, so every part of a page shares its reads. On the server every call makes a new one: a
 * cache kept there would hand one person's answers to another. A server render that reads ahead for the page uses the request's
 * own client (lib/server/queryClient.ts), never this.
 */
export function getQueryClient(): QueryClient {
  if (environmentManager.isServer()) return makeQueryClient();
  browserClient ??= makeQueryClient();
  return browserClient;
}

/**
 * A write to a job: what was read about it, and the lists and counts it is on, are re-read the next time a part asks (nothing is
 * re-read now; a write that moves the job reads the lists it touched again at once, lib/jobs/list/useJobLists.ts refreshJobLists).
 */
export function markJobChanged(id: string): void {
  if (environmentManager.isServer()) return;
  const client = getQueryClient();
  for (const queryKey of [
    queryKeys.job(id),
    queryKeys.jobLists,
    queryKeys.installSummaries,
    queryKeys.closeoutSummaries,
  ])
    void client.invalidateQueries({ queryKey, refetchType: "none" });
  // The navigation's counts are always on screen: they are read again now.
  void client.invalidateQueries({ queryKey: queryKeys.jobCounts, refetchType: "active" });
}
